import React, { createContext, useContext, useState, useEffect } from 'react';

const OrderContext = createContext();

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Load current user and their orders
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (currentUser?.id) {
      setCurrentUserId(currentUser.id);
      const userOrders = localStorage.getItem(`jrweb_user_${currentUser.id}_orders`);
      if (userOrders) {
        setOrders(JSON.parse(userOrders));
      }
    } else {
      setOrders([]);
    }
  }, []);

  // Save orders to localStorage whenever they change
  useEffect(() => {
    if (currentUserId) {
      localStorage.setItem(`jrweb_user_${currentUserId}_orders`, JSON.stringify(orders));
    }
  }, [orders, currentUserId]);

  // Update user ID when user logs in/out
  useEffect(() => {
    const handleStorageChange = () => {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (currentUser?.id !== currentUserId) {
        if (currentUser?.id) {
          setCurrentUserId(currentUser.id);
          const userOrders = localStorage.getItem(`jrweb_user_${currentUser.id}_orders`);
          if (userOrders) {
            setOrders(JSON.parse(userOrders));
          } else {
            setOrders([]);
          }
        } else {
          setCurrentUserId(null);
          setOrders([]);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUserId]);

  const createOrder = (cartItems, shippingInfo, paymentInfo) => {
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shipping = subtotal > 50 ? 0 : 9.99;
    const tax = subtotal * 0.08;
    const total = subtotal + shipping + tax;
    
    const newOrder = {
      id: `ORD-${Date.now()}`,
      orderNumber: `JR${Date.now()}`,
      date: new Date().toISOString(),
      items: cartItems,
      subtotal: subtotal,
      shipping: shipping,
      tax: tax,
      total: total,
      status: 'Processing',
      tracking: {
        ordered: new Date().toISOString(),
        processing: new Date().toISOString(),
        shipped: null,
        delivered: null
      },
      shippingInfo: shippingInfo,
      paymentInfo: paymentInfo
    };

    setOrders(prev => [newOrder, ...prev]);
    return newOrder;
  };

  const updateOrderStatus = (orderId, status, trackingUpdate = {}) => {
    setOrders(prev => prev.map(order => 
      order.id === orderId 
        ? { 
            ...order, 
            status,
            tracking: { ...order.tracking, ...trackingUpdate }
          }
        : order
    ));
  };

  const getOrderById = (orderId) => {
    return orders.find(order => order.id === orderId);
  };

  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  return (
    <OrderContext.Provider value={{
      orders,
      createOrder,
      updateOrderStatus,
      getOrderById,
      getOrdersByStatus
    }}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;
